/**
 * CarouselSwipe hook
 *
 * The home-page carousels (order-again, popular items, new restaurants) and
 * the cuisine chip bar are overflow-x scrollers. Chevrons are a mouse
 * affordance (hidden below `md` on the home carousels). Click-and-drag
 * (mouse, touch, or pen) pans the row and then rests on a `snap-start`
 * tile. Native overflow panning often never starts when the gesture
 * begins on a slide's <button>, <a>, or [role=button]; the hook captures
 * the pointer and pans `scrollLeft` itself. Axis lock is 8px so a
 * vertical drag can still scroll the page; pan and click-suppression
 * wait until 40px so a sloppy tap still activates the tile. Native
 * image/link dragging is cancelled on `dragstart` so a desktop drag
 * pans the row instead of starting HTML5 drag-and-drop. CSS
 * `snap-mandatory` is disabled while a RAF slide, a pointer drag, or a
 * mid-slide pointer-down is in flight so the browser cannot snap each
 * `scrollLeft` write back onto a tile. Pointer-down stops the RAF
 * without restoring snap; a click then rests on the nearest tile.
 *
 * `pointercancel` is not treated as a completed drag. The browser fires
 * it when native panning or page scrolling takes the gesture; snapping
 * there would fight the browser, and setting `swiped` would swallow the
 * next tap because canceled gestures have no trailing click.
 *
 * Pointer capture waits until the gesture locks to the horizontal axis
 * so a vertical drag can still scroll the page. Do not set
 * `touch-action: pan-x` on the scroller or its slides — that CSS also
 * blocks vertical page scrolling from the row.
 *
 * Home-page carousels also opt into autoplay with
 * `data-carousel-autoplay="true"`. The row waits `data-carousel-interval`
 * milliseconds (default 6000) after the previous slide finishes, then
 * advances a snap-aligned step (about 70% of the visible width, then rest
 * on a `snap-start` tile). The slide animation lasts
 * `data-carousel-duration` milliseconds (default 1200) instead of the
 * browser's short CSS `scroll-smooth`. Scheduling from the end of the
 * slide means a delay shorter than the duration cannot cancel the
 * in-flight RAF. Jumping `scrollLeft` back to 0 at the end rewinds
 * through every tile, fights `snap-mandatory`, and looks like a snap
 * back to the last slide. Instead the hook clones the slides once and
 * wraps by one set width after each step, so autoplay and chevrons read
 * as an endless loop. LiveView patches
 * strip those clones (they are not in the server HTML), so `updated()`
 * rebuilds them. Clones are `aria-hidden` and taken out of the tab order;
 * they are a visual copy, not a second set of controls. Autoplay pauses
 * while a pointer is down, while a hover-capable pointer is over the
 * carousel (including chevrons; skipped on coarse touch so iOS sticky
 * hover cannot leave autoplay paused after a tap), while a child is
 * focused, while the tab is hidden, while any slide has
 * `aria-expanded="true"` (Order it again cards expand in place; a tap
 * does not hover or focus on iOS), or when the user prefers reduced
 * motion. Cuisine chips and in-restaurant category nav do not set the
 * flag, so they do not auto-scroll. `updated()` cancels an in-flight
 * RAF before rebuilding clones so a LiveView patch cannot be overwritten
 * by the previous slide's frames.
 *
 * Attach to the scroller (needs a unique id):
 *
 *     <div id="popular-items-carousel-scroller" phx-hook="CarouselSwipe"
 *          data-carousel-autoplay="true"
 *          data-carousel-interval="6000"
 *          data-carousel-duration="1200">
 */
const AXIS_LOCK_PX = 8;
const SWIPE_THRESHOLD_PX = 40;
const SCROLL_FRACTION = 0.7;
const DEFAULT_AUTOPLAY_MS = 6000;
const DEFAULT_DURATION_MS = 1200;
const DRAG_SNAP_DURATION_MS = 280;
const END_EPSILON_PX = 8;
const SETTLE_BUFFER_MS = 80;

const CarouselSwipe = {
  mounted() {
    this.startX = 0;
    this.startY = 0;
    this.startScroll = 0;
    this.pointerId = null;
    this.axis = null;
    this.swiped = false;
    this.dragging = false;
    this.animating = false;
    this.animationStartedAt = null;
    this.animationDurationMs = 0;
    this.holdSnap = false;
    this.pointerActive = false;
    this.programmatic = false;
    this.programmaticTimer = null;
    this.scrollRaf = null;
    this.timer = null;
    this.pauseReasons = new Set();
    this.loopWidth = 0;
    this.rebuildingLoop = false;
    this.autoplay = this.el.dataset.carouselAutoplay === "true";
    const interval = Number(this.el.dataset.carouselInterval);
    this.autoplayMs =
      Number.isFinite(interval) && interval > 0
        ? interval
        : DEFAULT_AUTOPLAY_MS;
    const duration = Number(this.el.dataset.carouselDuration);
    this.scrollDurationMs =
      Number.isFinite(duration) && duration >= 0
        ? duration
        : DEFAULT_DURATION_MS;

    this.markProgrammatic = (durationMs) => {
      this.programmatic = true;
      if (this.programmaticTimer != null) {
        window.clearTimeout(this.programmaticTimer);
      }
      const ms = Number.isFinite(durationMs)
        ? durationMs
        : this.scrollDurationMs;
      const settleMs = Math.max(0, ms) + SETTLE_BUFFER_MS;
      this.programmaticTimer = window.setTimeout(() => {
        this.programmatic = false;
        this.programmaticTimer = null;
        this.wrapLoop();
      }, settleMs);
    };

    this.stopRaf = () => {
      if (this.scrollRaf != null) {
        window.cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = null;
      }
    };

    this.syncSnap = () => {
      this.el.style.scrollSnapType =
        this.dragging || this.animating || this.holdSnap ? "none" : "";
    };

    this.nowMs = () =>
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now();

    this.clearAnimationClock = () => {
      this.animationStartedAt = null;
      this.animationDurationMs = 0;
    };

    this.remainingAnimationMs = () => {
      if (!this.animating || this.reducedMotion()) return 0;
      const duration = this.animationDurationMs;
      const started = this.animationStartedAt;
      if (!Number.isFinite(duration) || duration <= 0 || started == null) {
        return 0;
      }
      return Math.max(0, duration - (this.nowMs() - started));
    };

    this.cancelScrollAnimation = () => {
      this.stopRaf();
      this.animating = false;
      this.clearAnimationClock();
      this.holdSnap = false;
      this.syncSnap();
    };

    this.originalSlides = () =>
      [...this.el.children].filter(
        (node) => !node.hasAttribute?.("data-carousel-clone"),
      );

    this.stripCloneIds = (root) => {
      if (root.id) root.removeAttribute("id");
      root.querySelectorAll?.("[id]").forEach((el) => el.removeAttribute("id"));
    };

    this.decorateClone = (root) => {
      root.setAttribute("data-carousel-clone", "true");
      root.setAttribute("aria-hidden", "true");
      this.stripCloneIds(root);
      if (root.matches?.("a, button, [href], [tabindex]")) {
        root.setAttribute("tabindex", "-1");
      }
      root
        .querySelectorAll?.(
          "a, button, input, select, textarea, [href], [tabindex]:not([tabindex='-1'])",
        )
        .forEach((el) => el.setAttribute("tabindex", "-1"));
    };

    this.withInstantScroll = (fn) => {
      const prev = this.el.style.scrollBehavior;
      this.el.style.scrollBehavior = "auto";
      fn();
      this.el.style.scrollBehavior = prev;
    };

    this.blurCloneFocus = () => {
      const active = document.activeElement;
      if (!active || !this.el.contains(active)) return;
      if (active.closest?.("[data-carousel-clone]")) active.blur();
    };

    this.rebuildLoop = (force = false) => {
      if (!this.autoplay) return;
      if (this.rebuildingLoop && !force) return;
      this.rebuildingLoop = true;
      this.el
        .querySelectorAll("[data-carousel-clone]")
        .forEach((node) => node.remove());
      this.loopWidth = 0;
      const originals = this.originalSlides();
      if (originals.length >= 2 && this.overflowing()) {
        originals.forEach((node) => {
          const clone = node.cloneNode(true);
          this.decorateClone(clone);
          this.el.appendChild(clone);
        });
        const first = originals[0];
        const firstClone = this.el.querySelector("[data-carousel-clone]");
        if (first && firstClone) {
          this.loopWidth = firstClone.offsetLeft - first.offsetLeft;
        }
      }
      const release = () => {
        this.rebuildingLoop = false;
        // Alpine 3.12 has no $cleanup magic, so the scroller cannot watch
        // childList itself. Tell the parent Alpine component that clones
        // (or their removal) changed overflowing / looping / chevron state.
        this.el.dispatchEvent(new Event("carousel-changed"));
      };
      if (typeof queueMicrotask === "function") queueMicrotask(release);
      else window.setTimeout(release, 0);
    };

    this.wrapLoop = () => {
      // Skip while a programmatic step is in flight. A backward step
      // teleports into the clone copy first; pointer-up would otherwise
      // subtract loopWidth in the same turn and land back at 0.
      if (!this.loopWidth || this.pointerActive || this.programmatic) return;
      if (this.el.scrollLeft < this.loopWidth) return;
      this.withInstantScroll(() => {
        while (this.el.scrollLeft >= this.loopWidth) {
          this.el.scrollLeft -= this.loopWidth;
        }
      });
      this.blurCloneFocus();
    };

    this.prepareLoopStep = (dir) => {
      if (!this.loopWidth) return;
      if (dir < 0 && this.el.scrollLeft <= END_EPSILON_PX) {
        this.withInstantScroll(() => {
          this.el.scrollLeft += this.loopWidth;
        });
      } else if (dir > 0 && this.el.scrollLeft >= this.loopWidth) {
        this.withInstantScroll(() => {
          this.el.scrollLeft -= this.loopWidth;
        });
      }
    };

    this.snapPoints = () =>
      [...this.el.children]
        .map((node) => node.offsetLeft)
        .sort((a, b) => a - b);

    this.snapTarget = (dir) => {
      const raw =
        this.el.scrollLeft + dir * this.el.clientWidth * SCROLL_FRACTION;
      const snaps = this.snapPoints();
      if (snaps.length === 0) return Math.max(0, raw);
      if (dir > 0) {
        const snapped = snaps.find((left) => left >= raw - 1);
        return snapped ?? snaps[snaps.length - 1];
      }
      const candidates = snaps.filter((left) => left <= raw + 1);
      return candidates.length ? candidates[candidates.length - 1] : snaps[0];
    };

    this.nearestSnap = () => {
      const snaps = this.snapPoints();
      const x = this.el.scrollLeft;
      if (snaps.length === 0) return Math.max(0, x);
      return snaps.reduce((best, left) =>
        Math.abs(left - x) < Math.abs(best - x) ? left : best,
      );
    };

    this.animateScrollTo = (left, durationMs) => {
      this.stopRaf();
      const from = this.el.scrollLeft;
      const to = left;
      const requested = Number.isFinite(durationMs)
        ? durationMs
        : this.scrollDurationMs;
      const duration = this.reducedMotion() ? 0 : requested;
      this.animating = duration > 0 && Math.abs(to - from) >= 1;
      this.syncSnap();
      if (!this.animating) {
        this.clearAnimationClock();
        this.withInstantScroll(() => {
          this.el.scrollLeft = to;
        });
        this.syncSnap();
        return;
      }
      const start = this.nowMs();
      this.animationStartedAt = start;
      this.animationDurationMs = duration;
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - (1 - t) * (1 - t);
        this.withInstantScroll(() => {
          this.el.scrollLeft = from + (to - from) * eased;
        });
        if (t < 1) {
          this.scrollRaf = window.requestAnimationFrame(tick);
        } else {
          this.scrollRaf = null;
          this.animating = false;
          this.clearAnimationClock();
          this.syncSnap();
        }
      };
      this.scrollRaf = window.requestAnimationFrame(tick);
    };

    this.scrollStep = (dir) => {
      this.cancelScrollAnimation();
      this.markProgrammatic(this.scrollDurationMs);
      this.prepareLoopStep(dir);
      this.animateScrollTo(this.snapTarget(dir));
    };

    this.jumpToStart = () => {
      this.cancelScrollAnimation();
      this.markProgrammatic(0);
      // Home carousels use the `scroll-smooth` class. Direct assignment
      // still follows that CSS unless we override, then restore.
      this.withInstantScroll(() => {
        this.el.scrollLeft = 0;
      });
    };

    this.atEnd = () =>
      Math.ceil(this.el.scrollLeft + this.el.clientWidth) >=
      this.el.scrollWidth - END_EPSILON_PX;

    this.overflowing = () => this.el.scrollWidth > this.el.clientWidth + 1;

    this.reducedMotion = () =>
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.canAutoplay = () =>
      this.autoplay &&
      !this.reducedMotion() &&
      this.overflowing() &&
      this.pauseReasons.size === 0 &&
      document.visibilityState === "visible";

    this.clearTimer = () => {
      if (this.timer != null) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    };

    this.scheduleAutoplay = () => {
      this.clearTimer();
      if (!this.canAutoplay()) return;
      // Delay is idle time after the current slide. If a RAF is in
      // flight, wait only the remaining ms (drag-snap is 280ms; hover
      // resume can land mid-slide) so a delay < duration cannot cancel
      // it and we do not pad a short snap with a full slide duration.
      this.timer = window.setTimeout(() => {
        this.advanceAutoplay();
        this.scheduleAutoplay();
      }, this.autoplayMs + this.remainingAnimationMs());
    };

    this.advanceAutoplay = () => {
      if (!this.canAutoplay()) return;
      if (this.loopWidth) {
        this.scrollStep(1);
        return;
      }
      if (this.atEnd()) {
        this.jumpToStart();
      } else {
        this.scrollStep(1);
      }
    };

    this.addPause = (reason) => {
      this.pauseReasons.add(reason);
      this.clearTimer();
    };

    this.removePause = (reason) => {
      this.pauseReasons.delete(reason);
      this.scheduleAutoplay();
    };

    // Order-it-again cards expand in place (`aria-expanded`). Hover and
    // focus do not fire on a tap, so autoplay would otherwise advance
    // the row while the customer is still reading the item list. Ignore
    // expanded state on loop clones — they are a visual copy.
    this.syncExpandedPause = () => {
      const expanded = [
        ...this.el.querySelectorAll('[aria-expanded="true"]'),
      ].some((node) => !node.closest("[data-carousel-clone]"));
      if (expanded) this.addPause("expanded");
      else this.removePause("expanded");
    };

    this.setDragging = (on) => {
      this.dragging = on;
      this.el.style.cursor = on ? "grabbing" : "";
      this.syncSnap();
    };

    this.applyDragScroll = (dx) => {
      let next = this.startScroll - dx;
      if (this.loopWidth) {
        if (next < 0) {
          this.startScroll += this.loopWidth;
          next += this.loopWidth;
        }
        while (next >= this.loopWidth) {
          this.startScroll -= this.loopWidth;
          next -= this.loopWidth;
        }
      } else {
        next = Math.max(0, next);
      }
      this.withInstantScroll(() => {
        this.el.scrollLeft = next;
      });
    };

    this.endPointerInteraction = () => {
      if (this.pointerActive) {
        this.pointerActive = false;
        this.wrapLoop();
        if (this.autoplay) this.removePause("pointer");
      }
    };

    this.captureIfHorizontal = (pointerId) => {
      if (this.axis !== "x") return;
      try {
        if (!this.el.hasPointerCapture(pointerId)) {
          this.el.setPointerCapture(pointerId);
        }
      } catch (_err) {
        // Synthetic events cannot capture; bubbling still delivers move/up.
      }
    };

    this.onPointerDown = (event) => {
      // Stop the RAF but keep snap off until this pointer ends. Restoring
      // snap-mandatory here would jump a mid-slide row under the pointer.
      const midSlide = this.animating;
      this.stopRaf();
      this.animating = false;
      this.clearAnimationClock();
      this.holdSnap = midSlide;
      this.syncSnap();
      if (this.autoplay) {
        this.pointerActive = true;
        this.addPause("pointer");
      } else {
        this.pointerActive = true;
      }

      if (this.pointerId !== null) return;

      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.startScroll = this.el.scrollLeft;
      this.axis = null;
      this.swiped = false;
      this.setDragging(false);
    };

    this.onPointerMove = (event) => {
      if (event.pointerId !== this.pointerId) return;

      const dx = event.clientX - this.startX;
      const dy = event.clientY - this.startY;

      if (!this.axis) {
        if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) return;
        this.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (this.axis === "x" && this.overflowing()) {
          this.captureIfHorizontal(event.pointerId);
        }
      }

      if (this.axis !== "x" || !this.overflowing()) return;

      this.stopRaf();
      this.animating = false;
      this.clearAnimationClock();
      if (typeof event.preventDefault === "function") event.preventDefault();
      // Axis lock is 8px so vertical page scroll still wins. Pan and click
      // suppression wait for a real drag so a sloppy tap still activates
      // a chip, restaurant, or reorder control.
      if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
      this.holdSnap = false;
      this.setDragging(true);
      this.swiped = true;
      this.applyDragScroll(dx);
    };

    this.onPointerUp = (event) => {
      const tracked =
        this.pointerId !== null && event.pointerId === this.pointerId;
      const mouseHold = this.pointerActive && this.pointerId === null;

      if (tracked) {
        const wasDragging = this.dragging;
        if (wasDragging) {
          // Wrap before snap so we don't animate onto a clone, then jump.
          // Drag-end snap stays short; autoplay/chevron speed is per-row.
          // Keep snap off across setDragging(false) → animateScrollTo.
          this.pointerActive = false;
          this.wrapLoop();
          this.holdSnap = false;
          this.animating = true;
          this.setDragging(false);
          this.markProgrammatic(DRAG_SNAP_DURATION_MS);
          this.animateScrollTo(this.nearestSnap(), DRAG_SNAP_DURATION_MS);
          if (this.autoplay) this.removePause("pointer");
        } else {
          this.setDragging(false);
          if (this.holdSnap) {
            this.holdSnap = false;
            this.markProgrammatic(DRAG_SNAP_DURATION_MS);
            this.animateScrollTo(this.nearestSnap(), DRAG_SNAP_DURATION_MS);
          }
        }
        this.release(event.pointerId);
      } else if (!mouseHold) {
        return;
      }

      this.endPointerInteraction();
    };

    this.onPointerCancel = (event) => {
      const tracked =
        this.pointerId !== null && event.pointerId === this.pointerId;
      const mouseHold = this.pointerActive && this.pointerId === null;

      if (!tracked && !mouseHold) return;

      // pointercancel means the browser took the gesture (native pan,
      // scroll, or an interruption). Do not snap — scrollLeft may
      // not have updated yet. Do not set `swiped`; canceled gestures
      // do not produce the click that would clear it.
      this.swiped = false;
      this.holdSnap = false;
      this.setDragging(false);
      if (tracked) this.release(event.pointerId);
      this.endPointerInteraction();
    };

    this.onClickCapture = (event) => {
      if (!this.swiped) return;
      event.preventDefault();
      event.stopPropagation();
      this.swiped = false;
    };

    this.release = (pointerId) => {
      try {
        if (pointerId != null && this.el.hasPointerCapture(pointerId)) {
          this.el.releasePointerCapture(pointerId);
        }
      } catch (_err) {
        // Capture was never taken.
      }
      this.pointerId = null;
      this.axis = null;
    };

    this.onCarouselStep = (event) => {
      const dir = Number(event.detail?.dir);
      if (dir !== 1 && dir !== -1) return;
      event.preventDefault?.();
      this.scrollStep(dir);
    };

    this.onDragStart = (event) => event.preventDefault();

    this.el.addEventListener("pointerdown", this.onPointerDown);
    this.el.addEventListener("pointermove", this.onPointerMove, {
      passive: false,
    });
    this.el.addEventListener("pointerup", this.onPointerUp);
    this.el.addEventListener("pointercancel", this.onPointerCancel);
    document.addEventListener("pointerup", this.onPointerUp);
    document.addEventListener("pointercancel", this.onPointerCancel);
    this.el.addEventListener("click", this.onClickCapture, true);
    this.el.addEventListener("carousel-step", this.onCarouselStep);
    this.el.addEventListener("dragstart", this.onDragStart);

    if (this.autoplay) {
      this.hoverRoot = this.el.parentElement;
      this.hoverCapable = () => window.matchMedia("(hover: hover)").matches;
      this.onHoverEnter = () => {
        if (!this.hoverCapable()) return;
        this.addPause("hover");
      };
      this.onHoverLeave = () => this.removePause("hover");
      this.onFocusIn = () => this.addPause("focus");
      this.onFocusOut = (event) => {
        if (!this.el.contains(event.relatedTarget)) this.removePause("focus");
      };
      this.onVisibility = () => {
        if (document.visibilityState === "hidden") this.addPause("hidden");
        else this.removePause("hidden");
      };
      this.onUserScroll = () => {
        if (this.programmatic || this.dragging) return;
        this.wrapLoop();
        this.scheduleAutoplay();
      };
      this.onMotionChange = () => this.scheduleAutoplay();

      if (this.hoverRoot) {
        this.hoverRoot.addEventListener("mouseenter", this.onHoverEnter);
        this.hoverRoot.addEventListener("mouseleave", this.onHoverLeave);
      }
      this.el.addEventListener("focusin", this.onFocusIn);
      this.el.addEventListener("focusout", this.onFocusOut);
      this.el.addEventListener("scroll", this.onUserScroll, { passive: true });
      document.addEventListener("visibilitychange", this.onVisibility);

      this.motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener("change", this.onMotionChange);
      } else {
        this.motionQuery.addListener(this.onMotionChange);
      }

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.rebuildLoop();
          this.syncExpandedPause();
          this.scheduleAutoplay();
        });
        this.resizeObserver.observe(this.el);
      }

      this.mutationObserver = new MutationObserver((mutations) => {
        if (this.rebuildingLoop) return;
        const serverChanged = mutations.some((mutation) => {
          if (mutation.type !== "childList") return false;
          return [...mutation.addedNodes, ...mutation.removedNodes].some(
            (node) =>
              node.nodeType === 1 &&
              !node.hasAttribute?.("data-carousel-clone"),
          );
        });
        // LiveView morphs omit clones, so a clone-only removal is still a
        // broken loop: loopWidth stays set and autoplay will not rewind.
        const clonesMissing =
          this.loopWidth > 0 && !this.el.querySelector("[data-carousel-clone]");
        if (!serverChanged && !clonesMissing) return;
        this.rebuildLoop();
        this.syncExpandedPause();
        this.scheduleAutoplay();
      });
      this.mutationObserver.observe(this.el, { childList: true });

      // Separate from childList so expanding a card (aria-expanded, and
      // Alpine x-show on descendants) cannot look like a server morph and
      // rebuild the loop copy.
      this.expandedObserver = new MutationObserver(() => {
        this.syncExpandedPause();
      });
      this.expandedObserver.observe(this.el, {
        subtree: true,
        attributes: true,
        attributeFilter: ["aria-expanded"],
      });

      this.rebuildLoop();
      this.syncExpandedPause();
      this.scheduleAutoplay();
    }
  },

  updated() {
    if (!this.autoplay || !this.rebuildLoop) return;
    this.cancelScrollAnimation();
    const previousLoopWidth = this.loopWidth;
    const previousScroll = this.el.scrollLeft;
    this.rebuildLoop(true);
    if (this.loopWidth && previousLoopWidth) {
      const offset = previousScroll % previousLoopWidth;
      this.withInstantScroll(() => {
        this.el.scrollLeft = offset;
      });
    }
    this.syncExpandedPause?.();
    this.scheduleAutoplay();
  },

  destroyed() {
    this.clearTimer?.();
    this.cancelScrollAnimation?.();
    if (this.programmaticTimer != null) {
      window.clearTimeout(this.programmaticTimer);
      this.programmaticTimer = null;
    }
    this.el.removeEventListener("pointerdown", this.onPointerDown);
    this.el.removeEventListener("pointermove", this.onPointerMove);
    this.el.removeEventListener("pointerup", this.onPointerUp);
    this.el.removeEventListener("pointercancel", this.onPointerCancel);
    document.removeEventListener("pointerup", this.onPointerUp);
    document.removeEventListener("pointercancel", this.onPointerCancel);
    this.el.removeEventListener("click", this.onClickCapture, true);
    this.el.removeEventListener("carousel-step", this.onCarouselStep);
    this.el.removeEventListener("dragstart", this.onDragStart);

    if (this.autoplay) {
      if (this.hoverRoot) {
        this.hoverRoot.removeEventListener("mouseenter", this.onHoverEnter);
        this.hoverRoot.removeEventListener("mouseleave", this.onHoverLeave);
      }
      this.el.removeEventListener("focusin", this.onFocusIn);
      this.el.removeEventListener("focusout", this.onFocusOut);
      this.el.removeEventListener("scroll", this.onUserScroll);
      document.removeEventListener("visibilitychange", this.onVisibility);
      if (this.motionQuery) {
        if (this.motionQuery.removeEventListener) {
          this.motionQuery.removeEventListener("change", this.onMotionChange);
        } else {
          this.motionQuery.removeListener(this.onMotionChange);
        }
      }
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.expandedObserver?.disconnect();
    }
  },
};

export default CarouselSwipe;
